/**
 * Axios HTTP client for the ARIA Training Portal.
 *
 * Design decisions (see ADR-009):
 * - Request interceptor injects `Authorization: Bearer <token>` from sessionStorage.
 *   sessionStorage (not localStorage) per CLAUDE.md — clears on tab close.
 * - Response interceptor handles global 401: clears session + redirects to /app/login.
 *   This means individual pages do not need to handle session expiry themselves.
 * - No default Content-Type header: axios infers it correctly (application/json for
 *   plain objects, multipart/form-data with boundary for FormData).
 *
 * NOTE: This client is for REST calls only. WebSocket connections are managed
 * directly in SessionPage using the browser's native WebSocket API.
 *
 * Usage:
 *   import apiClient from '../api/client';
 *   const res = await apiClient.post<ApiResponse<StartSessionResponseData>>('/sessions', body);
 */

import axios from 'axios';

const apiClient = axios.create({
  baseURL: '/api/v1',
  timeout: 30_000,
});

// ---------------------------------------------------------------------------
// Request: inject Bearer token
// ---------------------------------------------------------------------------

apiClient.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Response: global 401 → session expiry
// ---------------------------------------------------------------------------

apiClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      // Do not redirect or clear session if the request was for the public tenant configuration
      const url = error.config?.url ?? '';
      if (!url.endsWith('/tenant/config')) {
        sessionStorage.clear();
        // Hard redirect preserves the /app/ base path
        window.location.replace('/app/login');
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
